/**
 * Night Audio - Sleep Integration
 * 
 * This is the subconscious layer
 * Tone: soft, spacious, almost like it's dissolving into them
 * 
 * Affirmation Direction:
 * - I am here
 * - I am safe in this moment
 * - Nothing is missing
 * - I return to myself
 * - I feel life as it is
 * - I am grounded in now
 * - Presence is natural to me
 * 
 * (no force — just gentle repetition)
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Play, Pause, Moon, Volume2, VolumeX, Heart } from 'lucide-react';
import EntryTransition from '../../components/EntryTransition';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Night Audio Affirmations - Day 1: The Return
// Soft, spacious, dissolving into them
const NIGHT_AFFIRMATIONS = [
  "I am here...",
  "I am safe in this moment...",
  "Nothing is missing...",
  "I return to myself...",
  "I feel life as it is...",
  "I am grounded in now...",
  "Presence is natural to me...",
  "I release the day...",
  "I am held...",
  "There is nothing to fix...",
  "My body knows how to rest...",
  "I surrender to stillness...",
  "I am exactly where I need to be...",
  "Peace flows through me...",
  "I am here...",
];

// Main Component
export const NightAudio = () => {
  const { dayNumber = '1' } = useParams();
  const navigate = useNavigate();
  
  const [showEntryTransition, setShowEntryTransition] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentAffirmationIndex, setCurrentAffirmationIndex] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showAnchor, setShowAnchor] = useState(false);
  
  const intervalRef = useRef(null);
  const totalDuration = 15 * 60 * 1000; // 15 minutes
  const affirmationInterval = totalDuration / NIGHT_AFFIRMATIONS.length;
  const startTimeRef = useRef(null);

  const handleEntryComplete = () => {
    setShowEntryTransition(false);
  };

  // Start playback
  const handlePlay = () => {
    setIsPlaying(true);
    startTimeRef.current = Date.now();
  };

  const handlePause = () => {
    setIsPlaying(false);
  };

  // Affirmation cycling
  useEffect(() => {
    if (!isPlaying) return;

    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      setProgress((elapsed / totalDuration) * 100);

      const newIndex = Math.min(
        Math.floor(elapsed / affirmationInterval),
        NIGHT_AFFIRMATIONS.length - 1
      );
      
      if (newIndex !== currentAffirmationIndex) {
        setCurrentAffirmationIndex(newIndex);
      }

      // Show anchor at the end (last 30 seconds)
      if (elapsed >= totalDuration - 30000) {
        setShowAnchor(true);
      }

      // Complete after duration
      if (elapsed >= totalDuration) {
        setIsPlaying(false);
        recordCompletion();
        clearInterval(intervalRef.current);
      }
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isPlaying, currentAffirmationIndex, affirmationInterval, totalDuration]);

  const recordCompletion = async () => {
    const token = localStorage.getItem('reclaim_token');
    if (!token) return;

    try {
      await fetch(`${API_URL}/api/presence-journey/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          day: parseInt(dayNumber),
          session: 'night'
        })
      });
    } catch (error) {
      console.error('Failed to record completion:', error);
    }
  };

  const handleClose = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    navigate('/reclaim-presence/program');
  }, [navigate]);

  const formatTime = (ms) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const elapsed = isPlaying && startTimeRef.current 
    ? Date.now() - startTimeRef.current 
    : 0;

  return (
    <div className="fixed inset-0 bg-[#030305] overflow-hidden">
      {/* Entry Transition */}
      <EntryTransition 
        isActive={showEntryTransition} 
        onComplete={handleEntryComplete}
        duration={2500}
      />

      {/* Very minimal night gradient */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(30,30,50,0.3) 0%, transparent 70%)'
        }}
      />

      {/* Stars effect (very subtle) */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-white/20 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              opacity: [0.05, 0.2, 0.05],
            }}
            transition={{
              duration: 3 + Math.random() * 3,
              repeat: Infinity,
              delay: Math.random() * 3,
            }}
          />
        ))}
      </div>

      {/* Close Button */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.3 }}
        whileHover={{ opacity: 1 }}
        onClick={handleClose}
        className="absolute top-6 left-6 z-30 p-2 rounded-full hover:bg-white/5 transition-colors duration-300"
        data-testid="night-close"
      >
        <X size={24} className="text-white" />
      </motion.button>

      {/* Mute Button */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.3 }}
        whileHover={{ opacity: 1 }}
        onClick={() => setIsMuted(!isMuted)}
        className="absolute top-6 right-6 z-30 p-2 rounded-full hover:bg-white/5 transition-colors duration-300"
        data-testid="night-mute"
      >
        {isMuted ? (
          <VolumeX size={20} className="text-white" />
        ) : (
          <Volume2 size={20} className="text-white" />
        )}
      </motion.button>

      {/* Main Content */}
      <div className="flex flex-col items-center justify-center h-full px-8">
        {/* Moon Icon */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: showEntryTransition ? 0 : 1, y: showEntryTransition ? 20 : 0 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="mb-10"
        >
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-slate-700/30 to-slate-800/30 flex items-center justify-center border border-slate-600/20">
            <Moon size={36} className="text-slate-400" />
          </div>
        </motion.div>

        {/* Title */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: showEntryTransition ? 0 : 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="text-center mb-6"
        >
          <p className="text-slate-500/60 text-[10px] uppercase tracking-[0.3em] mb-3">
            Sleep Integration · The Return
          </p>
          <h1 className="font-['Playfair_Display'] text-2xl text-white/80">
            Dissolve into rest
          </h1>
        </motion.div>

        {/* Current Affirmation */}
        <div className="h-24 flex items-center justify-center mb-10">
          <AnimatePresence mode="wait">
            {isPlaying && (
              <motion.div
                key={currentAffirmationIndex}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 2 }}
                className="text-center"
              >
                <p 
                  className="text-white/50 text-xl font-light italic leading-relaxed max-w-sm"
                  style={{ textShadow: '0 0 30px rgba(100,100,150,0.2)' }}
                >
                  {NIGHT_AFFIRMATIONS[currentAffirmationIndex]}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Closing Anchor (appears at end) */}
        <AnimatePresence>
          {showAnchor && isPlaying && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="absolute top-1/4 left-1/2 -translate-x-1/2"
            >
              <motion.div
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="w-16 h-16 rounded-full bg-slate-600/20 flex items-center justify-center border border-slate-500/30"
              >
                <Heart size={28} className="text-slate-400" fill="rgba(148,163,184,0.3)" />
              </motion.div>
              <p className="text-slate-400/50 text-xs text-center mt-3 tracking-widest">
                I am here
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Play/Pause Button */}
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: showEntryTransition ? 0 : 1 }}
          transition={{ duration: 0.5, delay: 0.7 }}
          onClick={isPlaying ? handlePause : handlePlay}
          className="w-20 h-20 rounded-full bg-gradient-to-br from-slate-700/40 to-slate-800/40 flex items-center justify-center border border-slate-600/30 hover:border-slate-500/40 transition-all mb-8"
          data-testid="night-play-btn"
        >
          {isPlaying ? (
            <Pause size={28} className="text-white/70" />
          ) : (
            <Play size={28} className="text-white/70 ml-1" />
          )}
        </motion.button>

        {/* Progress / Time */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: showEntryTransition ? 0 : 0.5 }}
          className="text-center"
        >
          <p className="text-slate-500 text-sm">
            {isPlaying ? formatTime(elapsed) : '0:00'} / 15:00
          </p>
          
          {/* Progress Bar */}
          <div className="w-48 h-1 bg-slate-800 rounded-full mt-3 overflow-hidden">
            <motion.div
              className="h-full bg-slate-600 rounded-full"
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </motion.div>

        {/* Instructions */}
        {!isPlaying && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.4 }}
            transition={{ delay: 1 }}
            className="mt-12 text-center max-w-xs"
          >
            <p className="text-slate-500 text-xs">
              Press play, put your device aside, and let the affirmations dissolve into you as you drift to sleep
            </p>
            <p className="text-slate-600 text-xs mt-4 italic">
              No force — just gentle repetition
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default NightAudio;
